import { Alert, Button, Group, Stack, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { post } from './api';

const validCode = (value: string) => /^(?:[0-9]{6}|[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{8})$/.test(value);
const displayCode = (value: string) => value.length === 6 ? `${value.slice(0, 3)} ${value.slice(3)}` : `${value.slice(0, 4)}-${value.slice(4)}`;

// Public request code only. The requesting installation keeps its private proof.
export function pendingDeviceCode(): string {
  const code = location.pathname === '/connect' ? location.hash.slice(1).toUpperCase() : '';
  if (validCode(code)) {
    sessionStorage.setItem('device-approval', JSON.stringify({ code, until: Date.now() + 300_000 }));
    history.replaceState(null, '', '/connect');
    return code;
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem('device-approval') ?? 'null');
    if (saved?.until > Date.now() && typeof saved?.code === 'string' && validCode(saved.code)) return saved.code;
  } catch { /* An expired or incomplete browser request can be scanned again. */ }
  sessionStorage.removeItem('device-approval');
  return '';
}
export function DeviceApproval({ signedIn, code, onClose }: { signedIn: boolean; code: string; onClose: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const inspect = async () => {
    setBusy(true); setError('');
    try { setName((await post<{ name: string }>('/api/me/device-pairings/inspect', { code })).name); }
    catch { setError('Код истёк или запрос недоступен. Получите новый код на подключаемом устройстве.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (signedIn) void inspect(); }, [signedIn, code]);
  const close = () => { sessionStorage.removeItem('device-approval'); onClose(); };
  const approve = async () => {
    setBusy(true); setError('');
    try {
      await post('/api/me/device-pairings/approve', { code });
      sessionStorage.removeItem('device-approval'); setDone(true);
    } catch { setError('Не удалось подтвердить подключение. Проверьте список устройств в приложении; при необходимости получите новый код.'); }
    finally { setBusy(false); }
  };
  return <Stack gap="sm">
    <Title order={2}>{done ? 'Устройство подключено' : 'Подключить устройство'}</Title>
    {done ? <><Text>Вернитесь к подключаемому устройству — приложение завершит вход автоматически.</Text><Button variant="light" onClick={close}>Готово</Button></> : <>
      <Text>Код: {displayCode(code)}</Text>
      {!signedIn ? <><Text>Войдите в аккаунт, к которому хотите подключить устройство. После входа подтвердите запрос.</Text><Button component="a" href="/auth/login">Войти через Яндекс или VK</Button></> : name ? <>
        <Text>Подключить {name} к вашему аккаунту?</Text>
        <Text size="sm">Устройство получит отдельный доступ к VPN. Управлять аккаунтом и другими устройствами оно не сможет. Подтверждайте только код с вашего устройства.</Text>
        <Group><Button loading={busy} onClick={() => void approve()}>Подключить устройство</Button><Button variant="subtle" disabled={busy} onClick={close}>Отмена</Button></Group>
      </> : <Button variant="light" loading={busy} onClick={() => void inspect()}>Проверить запрос</Button>}
      {error && <Alert color="red">{error}</Alert>}
    </>}
  </Stack>;
}
